import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { TableCell } from '@anytag/shared-atoms';
import { usePostDetailsParam } from '@anytag/shared-hooks';
import { formatIntNumber, formatPercent } from '@anytag/shared-utils';
import { InstagramStoryPost, InstagramStoryPostSortField } from '@anytag/graphql';

export const useAnalyticsInstagramStoryPostsListTable = () => {
  const { t } = useTranslation();
  const { setPostDetailsParam } = usePostDetailsParam();

  const columns: ColumnDef<InstagramStoryPost>[] = [
    {
      accessorKey: 'post',
      meta: { sticky: true },
      header: t<string>('HeaderColumn.Story Post'),
      cell: ({ row }) => (
        <TableCell.Post
          title={row.original.content || ''}
          thumbnail={row.original.thumbnail || ''}
          onClick={() => setPostDetailsParam(row.original.id)}
        />
      )
    },
    {
      accessorKey: 'views',
      header: t<string>('HeaderColumn.View'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.ViewHelp'),
        sortField: InstagramStoryPostSortField.VIEWS
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.views)} />
    },
    {
      accessorKey: 'viewsByFollowersRate',
      header: t<string>('HeaderColumn.Views by Followers'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.viewsByFollowersHelp'),
        sortField: InstagramStoryPostSortField.VIEWS_BY_FOLLOWERS_RATE
      },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.viewsByFollowersRate, false)} unit="%" />
    },
    {
      accessorKey: 'viewsByNonFollowersRate',
      header: t<string>('HeaderColumn.Views by Non-Followers'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.viewsByNonFollowersHelp'),
        sortField: InstagramStoryPostSortField.VIEWS_BY_NON_FOLLOWERS_RATE
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.viewsByNonFollowersRate, false)} unit="%" />
      )
    },
    {
      accessorKey: 'reach',
      header: t<string>('HeaderColumn.Reach'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.ReachPostHelp'),
        sortField: InstagramStoryPostSortField.REACH
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.reach)} />
    },
    {
      accessorKey: 'interaction',
      header: t<string>('HeaderColumn.Interaction'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.interactionHelp'),
        sortField: InstagramStoryPostSortField.INTERACTION
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.interaction || 0)} />
    },
    {
      accessorKey: 'likes',
      header: t<string>('HeaderColumn.Likes'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.LikeHelp'),
        sortField: InstagramStoryPostSortField.LIKES
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.likes)} />
    },
    {
      accessorKey: 'reply',
      header: t<string>('HeaderColumn.Reply'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.CommentHelp'),
        sortField: InstagramStoryPostSortField.COMMENT
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.comments)} />
    },
    {
      accessorKey: 'share',
      header: t<string>('HeaderColumn.Share'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.shareHelp'),
        sortField: InstagramStoryPostSortField.SHARES
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.shares)} />
    },
    {
      accessorKey: 'accountsEngaged',
      header: t<string>('HeaderColumn.Accounts Engaged'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.accountsEngagedHelp'),
        sortField: InstagramStoryPostSortField.ACCOUNTS_ENGAGED
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.accountsEngaged)} />
    },
    {
      accessorKey: 'linkClicks',
      header: t<string>('HeaderColumn.Link Clicks'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.linkClicksHelp'),
        sortField: InstagramStoryPostSortField.LINK_CLICKS
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.linkClicks || 0)} />
    },
    {
      accessorKey: 'stickerTaps',
      header: t<string>('HeaderColumn.stickerTaps'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.stickerTapsHelp'),
        sortField: InstagramStoryPostSortField.STICKER_TAPS
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stickerTaps || 0)} />
    },
    {
      accessorKey: 'tapForward',
      header: t<string>('HeaderColumn.Tap Forward'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.TapForward'),
        sortField: InstagramStoryPostSortField.TAPS_FORWARD
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.tapsForward)} />
    },
    {
      accessorKey: 'tapBack',
      header: t<string>('HeaderColumn.Tap Back'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.TapBack'),
        sortField: InstagramStoryPostSortField.TAPS_BACK
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.tapsBack)} />
    },
    {
      accessorKey: 'swipeForward',
      header: t<string>('HeaderColumn.Swipe Forward'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.SwipeForward'),
        sortField: InstagramStoryPostSortField.SWIPE_FORWARD
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.swipeForward)} />
    },
    {
      accessorKey: 'exits',
      header: t<string>('HeaderColumn.Exits'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.ExitsHelp'),
        sortField: InstagramStoryPostSortField.EXITS
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.exits)} />
    },
    {
      accessorKey: 'profileActivity',
      header: t<string>('HeaderColumn.Profile Activity'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.profileActivityHelp'),
        sortField: InstagramStoryPostSortField.PROFILE_ACTIVITY
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.profileActivity)} />
    },
    {
      accessorKey: 'profileVisits',
      header: t<string>('HeaderColumn.Profile Visits'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.profileVisitsHelp'),
        sortField: InstagramStoryPostSortField.PROFILE_VISITS
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.profileVisits)} />
    },
    {
      accessorKey: 'follow',
      header: t<string>('HeaderColumn.Follow'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.FollowHelp'),
        sortField: InstagramStoryPostSortField.FOLLOW
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.follow || 0)} />
    },
    {
      accessorKey: 'productPageView',
      header: t<string>('HeaderColumn.Product Page View'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.productPageViewHelp'),
        sortField: InstagramStoryPostSortField.PRODUCT_PAGE_VIEW
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.productPageView || 0)} />
    },
    {
      accessorKey: 'productButtonClick',
      header: t<string>('HeaderColumn.Product Button Click'),
      meta: {
        align: 'right',
        help: t<string>('Tooltip.productButtonClickHelp'),
        sortField: InstagramStoryPostSortField.PRODUCT_BUTTON_CLICK
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.productButtonClick || 0)} />
    },
    {
      accessorKey: 'postDate',
      header: t<string>('HeaderColumn.Post date'),
      meta: { help: t<string>('Tooltip.PostDateHelp'), sortField: InstagramStoryPostSortField.POST_DATE },
      cell: ({ row }) => <TableCell.Date className="min-w-20" value={row.original.postDate} />
    }
  ];

  return { columns };
};
